import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Navbar, Footer, CustomButton, VideoModal } from '../components';
import { DemoExperience } from './DemoPage';
import { authAPI, avatarsAPI, plansAPI, clearAuthSession } from '../services/api';
import './LandingPage.css';

// Icons as components
const ShieldIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const UsersIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const HeartIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const MailIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const ChatIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const DocumentIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const JournalIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
  </svg>
);

const CheckIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasAvatars, setHasAvatars] = useState(false);
  const [checkingAvatars, setCheckingAvatars] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const plansFetchedRef = useRef(false); // Prevent duplicate API calls
  const isFetchingRef = useRef(false); // Track if currently fetching to prevent concurrent calls
  const userFetchedRef = useRef(false); // Prevent duplicate getCurrentUser calls
  const avatarsFetchedRef = useRef(false); // Prevent duplicate getAvatars calls
  const errorShownRef = useRef(false); // Prevent showing error toast multiple times

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [demoExpanded, setDemoExpanded] = useState(false);

  // Redirect to signup when invite link is used (?invite_id=...&invite_email=...)
  useEffect(() => {
    const inviteId = searchParams.get('invite_id');
    const inviteEmail = searchParams.get('invite_email');
    if (inviteId || inviteEmail) {
      const params = new URLSearchParams();
      if (inviteId) params.set('invite_id', inviteId);
      if (inviteEmail) params.set('invite_email', inviteEmail);
      const avatarId = searchParams.get('avatar_id');
      const iWillPay = searchParams.get('i_will_pay');
      if (avatarId) params.set('avatar_id', avatarId);
      if (iWillPay) params.set('i_will_pay', iWillPay);
      navigate(`/signup?${params.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    setIsLoggedIn(!!token);
    if (token) {
      checkUserAvatars();
    } else {
      setHasAvatars(false);
      setCheckingAvatars(false);
    }
    // Fetch plans on page load (only once)
    // Use both refs to prevent React StrictMode from causing duplicate calls
    // Check refs to prevent duplicate calls from React StrictMode
    if (plansFetchedRef.current || isFetchingRef.current) {
      console.log('LandingPage: Plans already fetched or currently fetching, skipping');
      return;
    }
    // Set fetching flag immediately to prevent concurrent calls (React StrictMode)
    isFetchingRef.current = true;
    fetchPlans(); // fetchPlans will set plansFetchedRef on success and reset isFetchingRef when done
  }, []);

  // Mirror Navbar when API layer clears an expired session
  useEffect(() => {
    const onSessionExpired = () => {
      setIsLoggedIn(false);
      setHasAvatars(false);
      userFetchedRef.current = false;
      avatarsFetchedRef.current = false;
    };
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => window.removeEventListener('auth:session-expired', onSessionExpired);
  }, []);

  // Fetch plans from API - try admin endpoint if authenticated, otherwise use public endpoint
  const fetchPlans = async () => {
    // Ref guards are set in useEffect, this function just makes the API call
    try {
      setPlansLoading(true);
      console.log('LandingPage: Fetching plan details from API (first time only)...');

      // Always use public endpoint (removed admin endpoint call)
      // This prevents unnecessary admin API calls when navigating from other pages
      const response = await plansAPI.getAllPlans();
      console.log('Plan Details API Response (Public):', response);

      // Handle different response structures
      let allPlans = [];
      if (response && response.data && response.data.plans) {
        allPlans = response.data.plans;
      }

      // Filter only active plans
      allPlans = allPlans.filter(plan => plan.is_active !== false);

      // Remove duplicates based on plan ID (if available) or name
      const uniquePlans = [];
      const seenIds = new Set();
      const seenNames = new Set();

      allPlans.forEach(plan => {
        const planId = plan.id || plan.plan_id;
        const planName = plan.name;

        // Use ID if available, otherwise use name
        if (planId) {
          if (!seenIds.has(planId)) {
            seenIds.add(planId);
            uniquePlans.push(plan);
          }
        } else if (planName) {
          if (!seenNames.has(planName)) {
            seenNames.add(planName);
            uniquePlans.push(plan);
          }
        } else {
          // If no ID or name, add it anyway (shouldn't happen)
          uniquePlans.push(plan);
        }
      });

      console.log('Fetched Plans (raw):', allPlans);
      console.log('Unique Plans (after deduplication):', uniquePlans);
      console.log('Number of plans (raw):', allPlans.length);
      console.log('Number of unique plans:', uniquePlans.length);
      if (uniquePlans.length > 0) {
        console.log('First plan structure:', {
          id: uniquePlans[0].id,
          name: uniquePlans[0].name,
          price_usd: uniquePlans[0].price_usd,
          plan_duration_days: uniquePlans[0].plan_duration_days,
          max_avatars: uniquePlans[0].max_avatars,
          is_active: uniquePlans[0].is_active
        });
      }
      setPlans(uniquePlans);
      plansFetchedRef.current = true; // Mark as fetched successfully after API call succeeds
      isFetchingRef.current = false; // Reset fetching flag on success
      console.log('LandingPage: Plans fetched successfully');
    } catch (error) {
      console.error('Error fetching plans:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        status: error.status || error.statusCode,
        response: error.response,
        stack: error.stack
      });

      // Keep empty array, will hide pricing section
      setPlans([]);
      // Reset refs on error so it can retry if needed
      plansFetchedRef.current = false;
      isFetchingRef.current = false;

      // Show error toast only once, not on every render
      if (!errorShownRef.current) {
        // Show more specific error messages based on error type
        let errorMessage = 'Unable to load pricing plans. The page will continue to work without pricing information.';
        const errorStatus = error?.status || error?.statusCode;

        if (errorStatus === 404) {
          errorMessage = 'Pricing plans endpoint not found. The page will continue to work without pricing information.';
        } else if (errorStatus >= 500) {
          errorMessage = 'Server error while loading plans. The page will continue to work without pricing information.';
        } else if (error.message && error.message.includes('CORS')) {
          errorMessage = 'Unable to connect to the API server. The page will continue to work without pricing information.';
        } else if (error.message && error.message.includes('Failed to fetch')) {
          errorMessage = 'Network error: Unable to reach the API server. The page will continue to work without pricing information.';
        } else if (error.message) {
          errorMessage = error.message;
        }

        // Show error toast with longer auto-close since it's informational
        toast.error(errorMessage, {
          toastId: 'landing-plans-load-error',
          autoClose: 5000
        });
        errorShownRef.current = true;
        // Reset after 30 seconds to allow showing again if needed
        setTimeout(() => {
          errorShownRef.current = false;
        }, 30000);
      }
    } finally {
      setPlansLoading(false);
    }
  };

  const checkUserAvatars = async () => {
    // Check if already fetched in this session (across page navigations)
    const userFetchedInSession = sessionStorage.getItem('user_fetched');
    const avatarsFetchedInSession = sessionStorage.getItem('avatars_fetched');

    // If both are already fetched in session (e.g., by Navbar), skip all API calls
    if (userFetchedInSession && avatarsFetchedInSession) {
      console.log('LandingPage: User and avatars already fetched in this session, skipping all API calls');
      // Get hasAvatars from sessionStorage if available
      const hasAvatarsValue = sessionStorage.getItem('has_avatars');
      if (hasAvatarsValue !== null) {
        setHasAvatars(hasAvatarsValue === 'true');
      }
      return;
    }

    // If already fetched by this component, skip
    if (userFetchedRef.current && avatarsFetchedRef.current) {
      console.log('LandingPage: Already fetched by this component, skipping API calls');
      return;
    }

    try {
      setCheckingAvatars(true);

      // Only call getCurrentUser if not already fetched anywhere
      let userId = null;
      if (!userFetchedRef.current && !userFetchedInSession) {
        // Set flag immediately to prevent duplicate calls
        userFetchedRef.current = true;
        sessionStorage.setItem('user_fetched', 'true'); // Mark in session immediately (synchronous)
        console.log('LandingPage: Fetching user data (first time only)...');
        const userResponse = await authAPI.getCurrentUser();
        if (userResponse.status && userResponse.data) {
          const userData = userResponse.data;
          userId = userData.id || userData.user_id;

          // Store full user data in sessionStorage for Navbar and other components
          try {
            sessionStorage.setItem('user_data', JSON.stringify(userData));
          } catch (error) {
            console.warn('Could not store user data in sessionStorage:', error);
          }

          // Store userId in sessionStorage so other components can use it
          if (userId) {
            sessionStorage.setItem('user_id', userId.toString());
          }
        }
      } else {
        console.log('LandingPage: User already fetched, skipping getCurrentUser call');
        // Get userId from sessionStorage if available (set by Navbar or previous fetch)
        const storedUserId = sessionStorage.getItem('user_id');
        if (storedUserId) {
          userId = parseInt(storedUserId, 10);
        }
      }

      // Only call getAvatars if userId is available and not already fetched
      if (userId && !avatarsFetchedRef.current && !avatarsFetchedInSession) {
        // Set flag immediately to prevent duplicate calls
        avatarsFetchedRef.current = true;
        sessionStorage.setItem('avatars_fetched', 'true'); // Mark in session immediately (synchronous)
        console.log('LandingPage: Fetching avatars (first time only)...');
        const avatarsResponse = await avatarsAPI.getAvatars(userId);
        if (avatarsResponse.status && avatarsResponse.data) {
          const avatars = avatarsResponse.data.avatars || [];
          const hasAvatarsValue = avatars.length > 0;
          setHasAvatars(hasAvatarsValue);
          sessionStorage.setItem('has_avatars', hasAvatarsValue.toString()); // Cache hasAvatars value

          // Store avatars data in sessionStorage
          try {
            sessionStorage.setItem('avatars_data', JSON.stringify(avatars));
          } catch (error) {
            console.warn('Could not store avatars data in sessionStorage:', error);
          }

          console.log('LandingPage: Avatars fetched successfully');
        }
      } else if (avatarsFetchedInSession) {
        console.log('LandingPage: Avatars already fetched in session, skipping getAvatars call');
        // Use cached value from sessionStorage
        const hasAvatarsValue = sessionStorage.getItem('has_avatars');
        if (hasAvatarsValue !== null) {
          setHasAvatars(hasAvatarsValue === 'true');
        }
      } else if (!userId) {
        console.warn('LandingPage: No userId available, cannot fetch avatars');
      }
    } catch (error) {
      console.error('Error checking avatars:', error);
      const status = error?.status || error?.statusCode;
      if (status === 401) {
        clearAuthSession();
        setIsLoggedIn(false);
        setHasAvatars(false);
        userFetchedRef.current = false;
        avatarsFetchedRef.current = false;
      } else {
        // Reset refs on error so it can retry if needed
        userFetchedRef.current = false;
        avatarsFetchedRef.current = false;
        sessionStorage.removeItem('user_fetched');
        sessionStorage.removeItem('avatars_fetched');
        sessionStorage.removeItem('has_avatars');
        sessionStorage.removeItem('user_id');
      }
    } finally {
      setCheckingAvatars(false);
    }
  };

  const handleCreateAvatarClick = (e) => {
    e.preventDefault();

    if (!isLoggedIn) {
      navigate('/signup');
      return;
    }

    // Always navigate to the creation page.
    // Even if they have avatars, the "Create Avatar" button should take them to the creation flow.
    navigate('/create-avatar');
  };

  const steps = [
    { number: 1, title: 'Create Account', desc: 'Sign up and choose a plan. Create an Avatar of yourself or someone you love.' },
    { number: 2, title: 'Upload Memories', desc: 'Share emails, letters, messages, and documents — anything that captures how they spoke and what they cared about.' },
    { number: 3, title: 'Build Their Avatar', desc: 'Our technology reads everything you\'ve shared and crafts an Avatar that reflects their voice, their personality, and the way they expressed themselves.' },
    { number: 4, title: 'Begin the Conversation', desc: 'Talk with their Avatar whenever you\'re ready. Invite family and friends so everyone can stay connected.' },
  ];

  const features = [
    { icon: <ShieldIcon />, title: 'Private & Secure', desc: 'Your memories are yours alone. Your data is never shared or used for training, you control who has access, and you can export or delete everything at any time.', variant: 'teal' },
    { icon: <UsersIcon />, title: 'Family Groups', desc: 'Invite the people who loved them too. Family members can each have their own conversations, or gather together in a shared group — everyone connects in the way that feels right for them.', variant: 'sage' },
    { icon: <HeartIcon />, title: 'Authentic Voice', desc: "Our AI captures how they truly spoke—their phrases, humor, and wisdom—so conversations feel genuine.", variant: 'lavender' },
  ];

  const dataTypes = [
    { icon: <MailIcon />, label: 'Emails' },
    { icon: <ChatIcon />, label: 'Messages' },
    { icon: <DocumentIcon />, label: 'Documents' },
    { icon: <JournalIcon />, label: 'Journals' },
  ];

  // Get pricing plans from API - map API response to display format
  const getPricingPlans = () => {
    // Return empty array if still loading or no plans from API
    if (plansLoading || plans.length === 0) {
      return [];
    }

    console.log('Mapping plans to display format. Plans count:', plans.length);
    console.log('Plans data:', plans);

    // Ensure no duplicates before mapping
    const uniquePlansMap = new Map();
    plans.forEach(plan => {
      const key = plan.id || plan.plan_id || plan.name;
      if (key && !uniquePlansMap.has(key)) {
        uniquePlansMap.set(key, plan);
      } else if (!key) {
        // If no unique identifier, add it (shouldn't happen)
        uniquePlansMap.set(`plan-${uniquePlansMap.size}`, plan);
      }
    });
    const uniquePlans = Array.from(uniquePlansMap.values());

    console.log('Unique plans after deduplication:', uniquePlans.length);
    if (plans.length !== uniquePlans.length) {
      console.warn('Duplicate plans detected! Original count:', plans.length, 'Unique count:', uniquePlans.length);
    }

    // Map API plan structure to display format
    // API structure: { id, plan_type_id, plan_interval, duration_days, price_usd, ... }
    const pricingPlans = uniquePlans.map(plan => {
      const price = parseFloat(plan.price_usd || plan.price || plan.amount || plan.plan_price || plan.price_amount || 0);
      const durationDays = plan.duration_days || plan.plan_duration_days || null;
      const interval = (plan.plan_interval || plan.interval || '').toLowerCase().trim();

      // Log plan interval for debugging
      console.log('Processing plan for period text:', {
        id: plan.id,
        plan_interval: plan.plan_interval,
        interval: plan.interval,
        parsed_interval: interval,
        duration_days: durationDays,
        plan_duration_days: plan.plan_duration_days
      });

      // Determine period based on plan_interval first, then duration_days
      let period = '';
      let periodText = '';

      // Check plan_interval first (most accurate)
      if (interval === 'yearly' || interval === 'year') {
        period = `/year`;
        periodText = `per year`;
        console.log(`Plan ${plan.id}: Using interval "yearly" → periodText: "${periodText}"`);
      } else if (interval === 'monthly' || interval === 'month') {
        period = `/month`;
        periodText = `per month`;
        console.log(`Plan ${plan.id}: Using interval "monthly" → periodText: "${periodText}"`);
      } else if (durationDays) {
        // Fallback to duration_days if interval is not specified
        if (durationDays >= 365) {
          const years = Math.floor(durationDays / 365);
          period = `/year`;
          periodText = `per year`;
          console.log(`Plan ${plan.id}: Using duration_days ${durationDays} (yearly) → periodText: "${periodText}"`);
        } else if (durationDays >= 28 && durationDays <= 31) {
          // Monthly: 28-31 days
          period = `/month`;
          periodText = `per month`;
          console.log(`Plan ${plan.id}: Using duration_days ${durationDays} (monthly) → periodText: "${periodText}"`);
        } else if (durationDays >= 30) {
          const months = Math.floor(durationDays / 30);
          period = `/month`;
          periodText = `per ${months} ${months === 1 ? 'month' : 'months'}`;
          console.log(`Plan ${plan.id}: Using duration_days ${durationDays} (${months} months) → periodText: "${periodText}"`);
        } else {
          period = `for ${durationDays} days`;
          periodText = `for ${durationDays} days`;
          console.log(`Plan ${plan.id}: Using duration_days ${durationDays} (custom) → periodText: "${periodText}"`);
        }
      } else {
        // Default to monthly if nothing specified
        period = `/month`;
        periodText = `per month`;
        console.log(`Plan ${plan.id}: No interval or duration found, defaulting to monthly → periodText: "${periodText}"`);
      }

      // Build features from plan limits
      const features = [];
      if (plan.max_avatars) {
        features.push(`${plan.max_avatars} avatar${plan.max_avatars > 1 ? 's' : ''}`);
      }
      if (plan.max_storage_gb) {
        features.push(`Up to ${plan.max_storage_gb}GB storage`);
      }
      if (plan.max_family_members) {
        features.push(`Up to ${plan.max_family_members} family members`);
      }
      if (plan.max_memory_chunks) {
        // UI copy: show memory as GB (per product wording)
        features.push(`${plan.max_memory_chunks} GB Memory`);
      }

      // Default features if none specified
      if (features.length === 0) {
        features.push('Full AI avatar features', 'Unlimited messages', 'Family sharing');
      }

      // Subtitle + price formatting to match the example landing page
      const nameLower = (plan.name || '').toString().toLowerCase();
      let subtitle = '';
      if (nameLower.includes('free')) {
        subtitle = durationDays ? `Try it for ${durationDays} days` : 'Try it free';
      } else if (nameLower.includes('personal') || nameLower.includes('myself') || nameLower.includes('individual')) {
        subtitle = 'For individual use';
      } else if (nameLower.includes('extended')) {
        subtitle = 'For larger families';
      } else if (nameLower.includes('family')) {
        subtitle = 'Share with loved ones';
      }

      const isFreeTrial = price === 0;
      const priceValue = isFreeTrial ? '$0' : `$${price.toFixed(price % 1 === 0 ? 0 : 2)}`;
      const pricePeriod = isFreeTrial ? (durationDays ? `for ${durationDays} days` : '') : period;

      return {
        name: plan.name || 'Plan',
        desc: periodText,
        subtitle,
        priceValue,
        pricePeriod,
        isFreeTrial,
        features: features,
        featured: !isFreeTrial && nameLower.includes('family'), // highlight family as "most popular" when present
        originalPlan: plan // Keep reference to original plan data
      };
    });

    // Sort: free plans first, then by price
    pricingPlans.sort((a, b) => {
      if (a.isFreeTrial) return -1;
      if (b.isFreeTrial) return 1;
      const priceA = parseFloat((a.priceValue || '').replace('$', '')) || 0;
      const priceB = parseFloat((b.priceValue || '').replace('$', '')) || 0;
      return priceA - priceB;
    });

    console.log('Mapped pricing plans:', pricingPlans);
    return pricingPlans;
  };

  const pricingPlans = getPricingPlans();

  return (
    <div className="landing-page">
      <Navbar variant="transparent" />

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="hero-background"></div>
        <Container fluid className="hero-content">
          <Row className="align-items-center">
            <Col lg={6} className="hero-text">
              <h1>Keep the<br />conversation <em>going</em></h1>
              <p>
                Create an Avatar of someone you love, built from their own words. Your family can talk with them, remember them, and keep them close — today and for years to come.
              </p>
              <div className="hero-buttons">
                {isLoggedIn ? (
                  <CustomButton
                    variant="gold"
                    onClick={handleCreateAvatarClick}
                    disabled={checkingAvatars}
                  >
                    {checkingAvatars ? 'Loading...' : hasAvatars ? 'Go to Dashboard' : 'Create Your First Avatar'}
                  </CustomButton>
                ) : (
                  <>
                    <Link to="/signup">
                      <CustomButton variant="gold">Create Your First Avatar</CustomButton>
                    </Link>
                  </>
                )}
                <CustomButton variant="secondary" onClick={() => setDemoExpanded(true)}>See How It Works</CustomButton>
              </div>
            </Col>

            <Col lg={6} className="hero-visual">
              {/* Embedded interactive demo. The backdrop div is always in the
                  tree (shown via CSS only when expanded) so the DemoExperience
                  keeps its position — and therefore its conversation state —
                  when toggling between compact and expanded. */}
              <div className={`hero-demo${demoExpanded ? ' is-expanded' : ''}`}>
                <div
                  className="hero-demo-backdrop"
                  onClick={() => setDemoExpanded(false)}
                  aria-hidden="true"
                />
                <div className="hero-demo-frame">
                  <DemoExperience
                    compact={!demoExpanded}
                    expanded={demoExpanded}
                    onToggleExpand={() => setDemoExpanded((p) => !p)}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works Section */}
      <section className="how-section section-padding">
        <Container>
          <div className="section-header">
            <div className="section-tag">How It Works</div>
            <h2 className="section-title">From their words to your next conversation</h2>
          </div>

          <Row className="steps-grid">
            {steps.map((step) => (
              <Col md={6} lg={3} key={step.number} className="mb-4">
                <div className="step-card">
                  <div className="step-number">{step.number}</div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-desc">{step.desc}</p>
                </div>
              </Col>
            ))}
          </Row>

          {/* Homepage video — relocated here from the hero's See How It Works button */}
          <div className="video-cta">
            <CustomButton variant="secondary" onClick={() => setShowVideoModal(true)}>
              Watch: why families choose SayAgain
            </CustomButton>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section section-padding">
        <Container>
          <div className="section-header">
            <div className="section-tag">Features</div>
            <h2 className="section-title">Private by design, built for meaningful connection</h2>
          </div>

          <Row className="features-grid">
            {features.map((feature, index) => (
              <Col md={4} key={index} className="mb-4">
                <div className="feature-card">
                  <div className={`feature-icon feature-icon-${feature.variant}`}>
                    {feature.icon}
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-desc">{feature.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Data Types Section */}
      <section className="data-section section-padding">
        <Container>
          <div className="section-header">
            <div className="section-tag">What You Can Share</div>
            <h2 className="section-title">The more you share, the richer the connection</h2>
          </div>

          <Row className="data-grid justify-content-center">
            {dataTypes.map((dataType, index) => (
              <Col xs={6} sm={3} key={index} className="mb-4">
                <div className="data-item">
                  <div className="data-icon">{dataType.icon}</div>
                  <div className="data-label">{dataType.label}</div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Pricing Section
      {pricingPlans.length > 0 && (
        <section className="pricing-section section-padding">
          <Container>
            <div className="section-header">
              <div className="section-tag">Pricing</div>
              <h2 className="section-title">Plans for every family</h2>
            </div>

            <Row className={`pricing-grid justify-content-center ${pricingPlans.length === 3 ? 'pricing-grid-three' : pricingPlans.length >= 4 ? 'pricing-grid-four' : ''}`}>
              {pricingPlans.map((plan, index) => {
                // Determine column width based on number of plans
                // 4+ plans: show 4 per row (lg={3}), 3 plans: show 3 per row (lg={4}), 2 plans: show 2 per row (lg={6}), 1 plan: full width (lg={12})
                let xs, sm, md, lg, xl;
                if (pricingPlans.length >= 4) {
                  // 4+ plans: show 4 per row on large screens
                  xs = 12; sm = 6; md = 6; lg = 3; xl = 3;
                } else if (pricingPlans.length === 3) {
                  // 3 plans: show 3 per row on large screens
                  xs = 12; sm = 6; md = 4; lg = 4; xl = 4;
                } else if (pricingPlans.length === 2) {
                  // 2 plans: show 2 per row
                  xs = 12; sm = 12; md = 6; lg = 6; xl = 6;
                } else {
                  // 1 plan: full width
                  xs = 12; sm = 12; md = 12; lg = 12; xl = 12;
                }

                return (
                  <Col
                    xs={xs}
                    sm={sm}
                    md={md}
                    lg={lg}
                    xl={xl}
                    key={plan.planTypeId || plan.originalPlan?.id || plan.name || index}
                    className="mb-4"
                  >
                    <div className={`price-card ${plan.featured ? 'featured' : ''}`}>
                      {plan.featured && <div className="price-badge">Most Popular</div>}
                      <div className="price-name">{plan.name}</div>
                      {plan.subtitle ? <div className="price-desc">{plan.subtitle}</div> : null}
                      <div className="price-amount">
                        <span className="price-value">{plan.priceValue}</span>
                        <span className="price-period">{plan.pricePeriod}</span>
                      </div>
                      <ul className="price-features">
                        {plan.features.map((feature, idx) => (
                          <li key={idx}>
                            <div className="check-icon"><CheckIcon /></div>
                            {feature}
                          </li>
                        ))}
                      </ul>
                      {isLoggedIn ? (
                        <CustomButton
                          variant={plan.featured ? 'primary' : 'secondary'}
                          fullWidth
                          onClick={handleCreateAvatarClick}
                          disabled={checkingAvatars}
                        >
                          {checkingAvatars ? 'Loading...' : hasAvatars ? 'Go to Dashboard' : 'Get Started'}
                        </CustomButton>
                      ) : (
                        <Link to="/signup">
                          <CustomButton
                            variant={plan.featured ? 'primary' : 'secondary'}
                            fullWidth
                          >
                            {plan.isFreeTrial ? 'Get started' : 'Choose plan'}
                          </CustomButton>
                        </Link>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Container>
        </section>
      )} */}

      {/* Testimonial Section */}
      <section className="testimonial-section section-padding">
        <Container>
          <div className="testimonial-card">
            <p className="testimonial-quote">
              "Being able to talk to Grandpa's Avatar has helped our whole family process our grief.
              It's not him, but it carries his wisdom forward in a way that feels genuine and comforting."
            </p>
            <div className="testimonial-author">
              <div className="testimonial-avatar">LR</div>
              <div>
                <div className="testimonial-name"></div>
                <div className="testimonial-title"></div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section section-padding">
        <Container>
          <div className="cta-content">
            <h2>Start preserving memories today</h2>
            <p>Create an Avatar of yourself or someone you love. Their voice, their wisdom, their way of expressing themselves — preserved for the people who need it most.</p>
            {isLoggedIn ? (
              <CustomButton
                variant="gold"
                size="lg"
                onClick={handleCreateAvatarClick}
                disabled={checkingAvatars}
              >
                {checkingAvatars ? 'Loading...' : hasAvatars ? 'Go to Dashboard' : 'Create Your First Avatar'}
              </CustomButton>
            ) : (
              <Link to="/signup">
                <CustomButton variant="gold" size="lg">Create Your First Avatar</CustomButton>
              </Link>
            )}
            <p className="cta-why-link">
              Not sure yet? <Link to="/why">Read the thinking behind SayAgain →</Link>
            </p>
          </div>
        </Container>
      </section>

      <Footer onHowItWorksClick={() => setShowVideoModal(true)} />
      
      <VideoModal
        show={showVideoModal}
        onHide={() => setShowVideoModal(false)}
        videoSrc={process.env.PUBLIC_URL + '/480p_SayAgain.mp4'}
      />
    </div>
  );
};

export default LandingPage;

